import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, XCircle, Edit, ArrowRight, ArrowLeft, Keyboard, ArrowUp, ArrowDown, Circle, ExternalLink, FileText, BookOpen, Mail, Sparkles } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { supabase } from '@/lib/supabase'
import { MarkdownRenderer } from '@/components/ui/markdown-renderer'
import { getEnhancementFromCache } from '@/lib/enhancement-cache'

interface FAQReviewCardProps {
  faq: any
  onApprove?: () => void // Optional - legacy support
  onReject?: () => void // Optional - legacy support
  onEdit: () => void
  onNext?: () => void
  onPrevious?: () => void
  hasNext?: boolean
  hasPrevious?: boolean
  currentIndex?: number
  totalCount?: number
  onStatusChange?: () => void // Callback for status updates
}

type ActionFeedback = 'approved' | 'rejected' | 'status_up' | 'status_down' | null

// Status flow configuration
const STATUS_FLOW = ['draft', 'review', 'approved', 'active'] as const
type StatusType = typeof STATUS_FLOW[number] | 'archived'

export function FAQReviewCard({
  faq,
  onApprove,
  onReject,
  onEdit,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  currentIndex,
  totalCount,
  onStatusChange
}: FAQReviewCardProps) {
  const [feedback, setFeedback] = useState<ActionFeedback>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [currentStatus, setCurrentStatus] = useState<StatusType>(faq.status)
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [sourceData, setSourceData] = useState<{ url: string | null; type: string | null; raw_content: string | null }>({ url: null, type: null, raw_content: null })
  const [loadingSource, setLoadingSource] = useState(false)
  const [cachedEnhancement, setCachedEnhancement] = useState<any>(null)

  // Update current status when faq changes
  useEffect(() => {
    setCurrentStatus(faq.status)
  }, [faq.id, faq.status])

  // Load source data when faq changes
  useEffect(() => {
    if (faq.source_uid) {
      loadSourceData(faq.source_uid)
    } else {
      setSourceData({ url: null, type: null, raw_content: null })
    }
  }, [faq.id, faq.source_uid])

  // Check for cached enhancement when FAQ changes
  useEffect(() => {
    const cached = getEnhancementFromCache(faq.id)
    setCachedEnhancement(cached)
  }, [faq.id])

  const loadSourceData = async (sourceUid: string) => {
    try {
      const { data, error } = await supabase
        .from('import_sources')
        .select('source_url, source_type')
        .eq('uid', sourceUid)
        .single()

      if (error) {
        console.error('Error loading source data:', error)
        return
      }

      if (data) {
        setSourceData({ url: data.source_url, type: data.source_type, raw_content: null })
      }
    } catch (error) {
      console.error('Error loading source data:', error)
    }
  }

  const loadSourceRawContent = async (sourceUid: string) => {
    if (sourceData.raw_content) return // Already loaded

    setLoadingSource(true)
    try {
      const { data, error } = await supabase
        .from('import_sources')
        .select('raw_content')
        .eq('uid', sourceUid)
        .single()

      if (error) throw error

      setSourceData(prev => ({ ...prev, raw_content: data.raw_content }))
    } catch (error) {
      console.error('Error loading raw content:', error)
    } finally {
      setLoadingSource(false)
    }
  }

  const handleOpenSourceModal = async () => {
    if (faq.source_uid) {
      await loadSourceRawContent(faq.source_uid)
      setShowSourceModal(true)
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          handleStatusUp()
          break
        case 'ArrowDown':
          e.preventDefault()
          handleStatusDown()
          break
        case 'ArrowRight':
          e.preventDefault()
          onNext?.()
          break
        case 'ArrowLeft':
          e.preventDefault()
          onPrevious?.()
          break
        case 'e':
        case 'E':
          e.preventDefault()
          onEdit()
          break
        case 's':
        case 'S':
          e.preventDefault()
          if (faq.source_uid) {
            handleOpenSourceModal()
          }
          break
        case '?':
          e.preventDefault()
          setShowShortcuts(true)
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [currentStatus, onEdit, onNext, onPrevious, faq.source_uid])

  const updateStatus = async (newStatus: StatusType) => {
    try {
      const { error } = await supabase
        .from('knowledge')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', faq.id)

      if (error) throw error

      setCurrentStatus(newStatus)
      onStatusChange?.() // Notify parent to refresh counts
      return true
    } catch (error) {
      console.error('Error updating status:', error)
      return false
    }
  }

  const handleStatusUp = async () => {
    if (currentStatus === 'archived') return // Can't move from archived

    const currentIdx = STATUS_FLOW.findIndex(s => s === currentStatus)
    if (currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1) {
      const newStatus = STATUS_FLOW[currentIdx + 1]
      setFeedback('status_up')
      const success = await updateStatus(newStatus)
      if (success) {
        await new Promise(resolve => setTimeout(resolve, 300))
        setFeedback(null)
      }
    }
  }

  const handleStatusDown = async () => {
    if (currentStatus === 'archived') return // Can't move from archived

    const currentIdx = STATUS_FLOW.findIndex(s => s === currentStatus)
    if (currentIdx > 0) {
      const newStatus = STATUS_FLOW[currentIdx - 1]
      setFeedback('status_down')
      const success = await updateStatus(newStatus)
      if (success) {
        await new Promise(resolve => setTimeout(resolve, 300))
        setFeedback(null)
      }
    }
  }

  const handleApprove = async () => {
    if (onApprove) {
      setFeedback('approved')
      await new Promise(resolve => setTimeout(resolve, 400))
      onApprove()
      setFeedback(null)
    }
  }

  const handleReject = async () => {
    if (onReject) {
      setFeedback('rejected')
      await new Promise(resolve => setTimeout(resolve, 400))
      onReject()
      setFeedback(null)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-missionSolar-primaryTeal dark:bg-missionSolar-primaryTeal text-white border-missionSolar-primaryTeal'
      case 'approved':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700'
      case 'review':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700'
      case 'draft':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
      case 'archived':
        return 'bg-gray-50 dark:bg-gray-900/20 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700'
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
    }
  }

  const getStatusDotColor = (status: string, isActive: boolean) => {
    if (!isActive) return 'bg-gray-300 dark:bg-gray-600 border-gray-400 dark:border-gray-500'

    switch (status) {
      case 'active':
        return 'bg-missionSolar-primaryTeal border-missionSolar-primaryTeal'
      case 'approved':
        return 'bg-green-500 border-green-600'
      case 'review':
        return 'bg-amber-500 border-amber-600'
      case 'draft':
        return 'bg-gray-400 border-gray-500'
      default:
        return 'bg-gray-400 border-gray-500'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft'
      case 'review': return 'Review'
      case 'approved': return 'Approved'
      case 'active': return 'Active'
      case 'archived': return 'Archived'
      default: return status
    }
  }

  const canMoveUp = () => {
    if (currentStatus === 'archived') return false // Can't move up from archived
    const currentIdx = STATUS_FLOW.findIndex(s => s === currentStatus)
    return currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1
  }

  const canMoveDown = () => {
    if (currentStatus === 'archived') return false // Can't move down from archived
    const currentIdx = STATUS_FLOW.findIndex(s => s === currentStatus)
    return currentIdx > 0
  }

  const getSourceIcon = (type: string | null) => {
    switch (type) {
      case 'neoom': return <BookOpen className="h-3 w-3" />
      case 'notion': return <FileText className="h-3 w-3" />
      case 'email': return <Mail className="h-3 w-3" />
      default: return <FileText className="h-3 w-3" />
    }
  }

  const getSourceName = (type: string | null) => {
    switch (type) {
      case 'neoom': return 'Neoom Wiki'
      case 'notion': return 'Notion Wiki'
      case 'email': return 'Email'
      default: return 'Source'
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-4"
      >
        {/* Context-Aware Header - Dynamically adapts to position */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            {/* Navigation Left */}
            {hasPrevious && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onPrevious}
                className="gap-1.5 h-8 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
            )}

            {/* Progress Counter */}
            {currentIndex !== undefined && totalCount !== undefined && (
              <span className="font-mono">
                {currentIndex + 1} <span className="text-muted-foreground/50">of</span> {totalCount}
                {currentIndex + 1 === totalCount && <span className="ml-1.5">✓</span>}
              </span>
            )}

            {/* Navigation Right */}
            {hasNext && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onNext}
                className="gap-1.5 h-8 text-muted-foreground hover:text-foreground"
              >
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Source Link */}
            {faq.source_uid && sourceData.url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenSourceModal}
                className="gap-1.5 h-8 text-muted-foreground hover:text-foreground"
                title="Quelle öffnen (S)"
              >
                {getSourceIcon(sourceData.type)}
                {getSourceName(sourceData.type)}
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}

            {/* Shortcuts */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShortcuts(true)}
              className="gap-1.5 h-8 text-muted-foreground hover:text-foreground"
            >
              <Keyboard className="h-3.5 w-3.5" />
              ?
            </Button>
          </div>
        </div>

        {/* Main Review Card with Ampel Overlay */}
        <AnimatePresence mode="wait">
          <motion.div
            key={feedback}
            initial={false}
            animate={
              feedback === 'status_up'
                ? { scale: 1.01, borderColor: 'rgb(34 197 94)' }
                : feedback === 'status_down'
                ? { scale: 0.99, borderColor: 'rgb(239 68 68)' }
                : { scale: 1 }
            }
            className={cn(
              'border-2 rounded-lg transition-colors relative',
              feedback === 'status_up' && 'border-green-500',
              feedback === 'status_down' && 'border-red-500',
              !feedback && 'border-border'
            )}
          >
            {/* Status Ampel - Floating Overlay (top-right) */}
            <div className="absolute top-4 right-4 z-10 flex flex-col items-center gap-1 p-2 border rounded-lg bg-background/95 backdrop-blur-sm shadow-lg">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleStatusUp}
                disabled={!canMoveUp()}
                className="h-7 w-7 p-0 hover:bg-green-100 dark:hover:bg-green-900/30"
                title="Status erhöhen (↑)"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>

              <div className="flex flex-col items-center gap-1.5 py-2">
                {[...STATUS_FLOW].reverse().map((status) => (
                  <div
                    key={status}
                    className={cn(
                      "w-4 h-4 rounded-full border-2 transition-all cursor-help",
                      getStatusDotColor(status, status === currentStatus),
                      status === currentStatus && 'scale-150 ring-2 ring-offset-2 ring-offset-background ring-current'
                    )}
                    title={getStatusLabel(status)}
                  />
                ))}
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleStatusDown}
                disabled={!canMoveDown()}
                className="h-7 w-7 p-0 hover:bg-red-100 dark:hover:bg-red-900/30"
                title="Status senken (↓)"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">{faq.question}</CardTitle>
                {faq.category && (
                  <Badge variant="secondary" className="w-fit">
                    {faq.category}
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Answer */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">Antwort</h3>
                  <div className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 dark:bg-muted/50 p-4 rounded-md">
                    <MarkdownRenderer content={faq.answer} />
                  </div>
                </div>

                {/* Tags */}
                {faq.tags && faq.tags.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {faq.tags.map((tag: string, idx: number) => (
                        <Badge key={idx} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Question Variations */}
                {faq.question_variations && faq.question_variations.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground">Frage-Variationen</h3>
                    <ul className="space-y-1 text-sm">
                      {faq.question_variations.map((variant: string, idx: number) => (
                        <li key={idx} className="text-muted-foreground">• {variant}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground border-t pt-4">
                  {faq.problem_type && (
                    <div>
                      <span className="font-medium">Problem Type:</span> {faq.problem_type}
                    </div>
                  )}
                  {faq.quality_score && (
                    <div>
                      <span className="font-medium">Quality Score:</span> {faq.quality_score}/100
                    </div>
                  )}
                  {faq.created_at && (
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {new Date(faq.created_at).toLocaleDateString('de-DE')}
                    </div>
                  )}
                  {faq.updated_at && (
                    <div>
                      <span className="font-medium">Updated:</span>{' '}
                      {new Date(faq.updated_at).toLocaleDateString('de-DE')}
                    </div>
                  )}
                </div>

                {/* Enhancement Preview */}
                {cachedEnhancement && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-yellow-500" />
                        AI-Enhancement verfügbar
                      </h3>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={onEdit}
                        className="gap-2 h-7 text-xs"
                      >
                        <Edit className="h-3 w-3" />
                        Bearbeiten & Anwenden
                      </Button>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {cachedEnhancement.enhanced?.tags && (
                          <div>
                            <span className="font-medium text-yellow-800 dark:text-yellow-200">Tags:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {cachedEnhancement.enhanced.tags.slice(0, 5).map((tag: string, i: number) => (
                                <Badge key={i} variant="secondary" className="text-[10px] h-5">
                                  {tag}
                                </Badge>
                              ))}
                              {cachedEnhancement.enhanced.tags.length > 5 && (
                                <Badge variant="secondary" className="text-[10px] h-5">
                                  +{cachedEnhancement.enhanced.tags.length - 5}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                        {cachedEnhancement.enhanced?.question_variations && (
                          <div>
                            <span className="font-medium text-yellow-800 dark:text-yellow-200">Variationen:</span>
                            <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                              {cachedEnhancement.enhanced.question_variations.length} neue Frage-Variationen
                            </p>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-yellow-600 dark:text-yellow-400 mt-2 flex items-center gap-1">
                        💡 Klicke auf "Bearbeiten & Anwenden" um Enhancement zu prüfen
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={onEdit}
            disabled={!!feedback}
            className="gap-2"
          >
            <Edit className="h-5 w-5" />
            Edit
          </Button>

          <Button
            variant="destructive"
            size="lg"
            onClick={async () => {
              const success = await updateStatus('archived')
              if (success) {
                onNext?.() // Move to next after archiving
              }
            }}
            disabled={!!feedback}
            className="gap-2"
          >
            <XCircle className="h-5 w-5" />
            Aussortieren
          </Button>
        </div>

        {/* Bottom Hint - Subtle */}
        <div className="text-center text-xs text-muted-foreground pt-2">
          <span className="font-mono">↑↓</span> Status · <span className="font-mono">←→</span> Navigate · <span className="font-mono">E</span> Edit · <span className="font-mono">S</span> Source
        </div>
      </motion.div>

      {/* Shortcuts Dialog */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
            <DialogDescription>Navigate and review FAQs faster with keyboard shortcuts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2 text-sm">🚦 Status Control (Ampel)</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 ml-2">
                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">↑</kbd>
                <span className="text-sm">Status erhöhen (draft → review → approved → active)</span>

                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">↓</kbd>
                <span className="text-sm">Status senken (zurückstufen)</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-sm">🔄 Navigation</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 ml-2">
                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">→</kbd>
                <span className="text-sm">Nächster Eintrag</span>

                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">←</kbd>
                <span className="text-sm">Vorheriger Eintrag</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-sm">✏️ Actions</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 ml-2">
                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">E</kbd>
                <span className="text-sm">Edit (bearbeiten)</span>

                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">S</kbd>
                <span className="text-sm">Source öffnen (Originalquelle)</span>

                <kbd className="px-2 py-1 text-xs font-semibold border rounded bg-muted">?</kbd>
                <span className="text-sm">Shortcuts anzeigen</span>
              </div>
            </div>

            <div className="text-xs text-muted-foreground border-t pt-3 mt-3">
              <strong>Status-Flow (↑ nach oben):</strong>
              <div className="flex flex-col items-start gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-missionSolar-primaryTeal text-white text-xs">Active</Badge>
                  <span className="text-xs">← Oben (Ziel: Production)</span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span>↑</span>
                  <Badge className="bg-green-100 text-green-800 border border-green-300 text-xs">Approved</Badge>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span>↑</span>
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-xs">Review</Badge>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span>↑</span>
                  <Badge className="bg-gray-100 text-gray-700 border border-gray-300 text-xs">Draft</Badge>
                  <span className="text-xs">← Unten (Start)</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Source Content Modal */}
      <Dialog open={showSourceModal} onOpenChange={setShowSourceModal}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              {getSourceIcon(sourceData.type)}
              {getSourceName(sourceData.type)}: {faq.source_uid}
            </DialogTitle>
            {sourceData.url && (
              <div className="flex items-center gap-2 mt-2">
                <a
                  href={sourceData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  {sourceData.url}
                </a>
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 px-6 pb-6">
            {loadingSource ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary mx-auto"></div>
                  <p className="text-sm text-muted-foreground">Lade Source Content...</p>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-[calc(95vh-120px)] w-full">
                <div className="bg-muted rounded-lg p-4">
                  <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                    {sourceData.raw_content || 'No raw content available'}
                  </pre>
                </div>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
